import type { ConsultationIntent } from '../core/agents/consultation-intent-router.js'
import {
  detectConsultationIntent,
  isExplicitBookingAction,
} from '../core/agents/consultation-intent-router.js'

/** Rollback flag — default off until PR2 cutover is proven in production. */
export function isLiveFirstFunnelEnabled(): boolean {
  const raw =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LIVE_FIRST_FUNNEL_ENABLED) ||
    (typeof process !== 'undefined' ? process.env.LIVE_FIRST_FUNNEL_ENABLED : undefined)

  if (raw === undefined || raw === '') return false
  const normalized = String(raw).trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

/** Server-only gate check (no import.meta). */
export function isLiveFirstFunnelEnabledOnServer(): boolean {
  const raw = process.env.LIVE_FIRST_FUNNEL_ENABLED
  if (raw === undefined || raw === '') return false
  const normalized = raw.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

/**
 * Intents the REST orchestrator must own. Live-first = typed turns ride the Live WS;
 * it does NOT mean orchestrator owns every turn. Native Live answers voice, vision,
 * googleSearch, general chat, and discovery-brief asks (`generate_discovery_report`
 * + client auto-run). Visual questions stay on Live 3.1 realtime frames;
 * REST 3.5 snapshot vision is for disconnected / upload analysis only.
 *
 * Do not steal `report_request` — the gate canned “AI Transformation Proposal”
 * copy and blocked the Live tool. Explicit book + weather stay forced.
 */
export const LIVE_FIRST_FORCED_GATE_INTENTS: ReadonlySet<ConsultationIntent> = new Set([
  'location_weather_question',
])

export function isLiveFirstForcedGateIntent(intent: ConsultationIntent): boolean {
  return LIVE_FIRST_FORCED_GATE_INTENTS.has(intent)
}

export function detectLiveFirstForcedGate(text: string): {
  forced: boolean
  intent: ConsultationIntent
  confidence: number
} {
  const detected = detectConsultationIntent(text)
  const explicitBook =
    detected.intent === 'booking_request' && isExplicitBookingAction(text)
  return {
    forced: isLiveFirstForcedGateIntent(detected.intent) || explicitBook,
    intent: detected.intent,
    confidence: detected.confidence,
  }
}

export const LIVE_FIRST_FALLBACK_MESSAGE_KEY = 'liveFirst.fallback'

export const LIVE_FIRST_SEND_STATUS_KEYS = {
  connecting: 'liveFirst.connecting',
  sending: 'liveFirst.sending',
  receiving: 'liveFirst.receiving',
} as const

/** @deprecated Use LIVE_FIRST_FALLBACK_MESSAGE_KEY with i18n at call sites. */
export const LIVE_FIRST_FALLBACK_MESSAGE =
  'Live session unavailable, continuing in fallback mode'

/** @deprecated Use LIVE_FIRST_SEND_STATUS_KEYS with i18n at call sites. */
export const LIVE_FIRST_SEND_STATUS = {
  connecting: 'Connecting Live…',
  sending: 'Sending message…',
  receiving: 'Receiving response…',
} as const

/** Budget guards for Live-first sessions (env overrides with sane defaults). */
export const LIVE_FIRST_COST_GUARDS = {
  idleTimeoutMs: parsePositiveInt(process.env.LIVE_FIRST_IDLE_TIMEOUT_MS, 5 * 60_000),
  maxSessionDurationMs: parsePositiveInt(process.env.LIVE_FIRST_MAX_SESSION_MS, 30 * 60_000),
  maxReconnects: parsePositiveInt(process.env.LIVE_FIRST_MAX_RECONNECTS, 5),
  maxToolCallsPerTurn: parsePositiveInt(process.env.LIVE_FIRST_MAX_TOOL_CALLS_PER_TURN, 6),
  maxReportAttemptsPerSession: parsePositiveInt(process.env.LIVE_FIRST_MAX_REPORT_ATTEMPTS, 3),
} as const

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}
